import { useEffect, useRef, useState } from 'react';
import { CONTACT_FORM_FIELDS, type SyncOp } from '@wr/shared';
import { ApiError, errorMessage, get, putFile } from '../../api.js';
import { ContactForm, EMPTY_CONTACT, type ContactDraft } from '../../components/ContactForm.js';
import { Icon } from '../../components/Icon.js';
import { useToast } from '../../components/Toast.js';
import { Notice } from '../../components/ui.js';
import { useOffline } from '../../offline/context.js';
import type { LocalStore } from '../../offline/store.js';
import { useSignedIn } from '../../session.js';
import {
  KnownContact,
  ProjectBlock,
  knownContactId,
  useKnownContact,
  focusFirst,
  leadOps,
  projectErrors,
  projectFrom,
  type ProjectDraft,
  type ProjectErrors,
} from '../leads/Project.js';
import { useStages } from '../leads/stages.js';
import { useAskEvent } from './AskEvent.js';
import { newId } from '../../offline/ids.js';

/** A scan being edited: nothing is saved as a contact before Save (Nov 3, 2026). */
export interface ScanDraft {
  /** The contact ID it will have once saved, and the reading's. */
  id: string;
  photo: Blob;
  edits: Partial<ContactDraft>;
  startedAt: number;
}

const DRAFT = 'scanDraft';
const DRAFT_PHOTO = 'scanDraftPhoto';
/** After this long without an answer, the fields unlock (decision of Nov 3, 2026). */
export const READING_WAIT_MS = 10_000;

/**
 * The scan being edited is kept on this device, encrypted, so nothing is
 * lost if the app closes; it is not a change to send, and it goes at Save
 * or Discard.
 */
export async function keepDraft(store: LocalStore, draft: ScanDraft, withPhoto = false) {
  if (withPhoto) {
    const bytes = new Uint8Array(await draft.photo.arrayBuffer());
    let binary = '';
    for (let i = 0; i < bytes.length; i += 0x8000) {
      binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
    }
    await store.setMeta(DRAFT_PHOTO, { type: draft.photo.type, data: btoa(binary) });
  }
  await store.setMeta(DRAFT, { id: draft.id, edits: draft.edits, startedAt: draft.startedAt });
}

export async function loadDraft(store: LocalStore): Promise<ScanDraft | null> {
  const meta = await store
    .getMeta<{ id: string; edits: Partial<ContactDraft>; startedAt: number }>(DRAFT)
    .catch(() => null);
  const photo = await store.getMeta<{ type: string; data: string }>(DRAFT_PHOTO).catch(() => null);
  if (!meta || !photo) return null;
  const binary = atob(photo.data);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return { ...meta, photo: new Blob([bytes], { type: photo.type || 'image/jpeg' }) };
}

export async function dropDraft(store: LocalStore | null) {
  await store?.setMeta(DRAFT, null).catch(() => undefined);
  await store?.setMeta(DRAFT_PHOTO, null).catch(() => undefined);
}

type Reading = 'reading' | 'done' | 'failed' | 'offline' | 'waited' | 'typing';
interface ReadAnswer {
  status: 'reading' | 'done' | 'failed';
  fields?: Record<string, string | null>;
  needsVerification?: string[];
}

/**
 * After Confirm (brief section 6.3, decisions of Nov 3, 2026). Nothing is
 * saved before Save: the photo waits on this device. With network, the
 * card is read at once: "Reading the card…", the fields locked, then all
 * filled at once; Type it myself does not wait; without network or after
 * 10 seconds, "Reading when network is back. You can type now." The
 * keyboard never opens by itself. Already a contact on the exact email:
 * Open this contact, or add a lead to it, never a second contact. One
 * compact bar at the bottom: Save, Scan next, Lead; the cross leaves (and
 * asks Discard this scan? as nothing is saved yet).
 */
export function ScanPerson({
  draft,
  photoUrl,
  onEdits,
  onSaved,
  onClose,
  onDiscard,
}: {
  draft: ScanDraft;
  photoUrl: string;
  /** Each change, kept on the device. */
  onEdits(edits: Partial<ContactDraft>): void;
  /** Saved: next card, or home. */
  onSaved(next: 'scan' | 'home'): void;
  /** The cross: asks before discarding. */
  onClose(): void;
  /** Leaves without saving (the person exists already). */
  onDiscard(to: string): void;
}) {
  const { user, can } = useSignedIn();
  const { store, saveScan } = useOffline();
  const askEvent = useAskEvent();
  const toast = useToast();
  const stages = useStages();
  const [reading, setReading] = useState<Reading>(() => (navigator.onLine ? 'reading' : 'offline'));
  const [read, setRead] = useState<Record<string, string | null>>({});
  const [toCheck, setToCheck] = useState<string[]>([]);
  const [edits, setEdits] = useState<Partial<ContactDraft>>(draft.edits);
  const [moreOpen, setMoreOpen] = useState(false);
  const [project, setProject] = useState(false);
  const [answers, setAnswers] = useState<ProjectDraft>(() => projectFrom());
  const [countryChosen, setCountryChosen] = useState(false);
  const [projectErrs, setProjectErrs] = useState<ProjectErrors>({});
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const canLead = can('leads.create');

  // The reading, sent once; its answer fills every field at once, whenever it comes.
  useEffect(() => {
    if (!navigator.onLine) return;
    let current = true;
    const waited = setTimeout(
      () => current && setReading((r) => (r === 'reading' ? 'waited' : r)),
      READING_WAIT_MS,
    );
    const apply = (a: ReadAnswer) => {
      if (!current) return;
      if (a.status === 'done') {
        setRead(a.fields ?? {});
        setToCheck(a.needsVerification ?? []);
        setReading('done');
      } else if (a.status === 'failed') {
        setReading('failed');
      }
    };
    void (async () => {
      try {
        let answer = await putFile<ReadAnswer>(`/capture/read/${draft.id}`, draft.photo, 'POST');
        // Still reading: asked again for two minutes at most.
        for (let i = 0; current && answer.status === 'reading' && i < 60; i++) {
          await new Promise((r) => setTimeout(r, 2_000));
          answer = await get<ReadAnswer>(`/capture/read/${draft.id}`);
        }
        apply(answer);
      } catch (error) {
        if (!current) return;
        setReading((r) =>
          r === 'typing'
            ? r
            : error instanceof ApiError && error.code === 'offline'
              ? 'offline'
              : 'failed',
        );
      }
    })();
    return () => {
      current = false;
      clearTimeout(waited);
    };
  }, [draft.id, draft.photo]);

  const locked = reading === 'reading';
  const fromCard: ContactDraft = {
    ...EMPTY_CONTACT,
    ...Object.fromEntries(Object.entries(read).filter(([k, v]) => v && k in EMPTY_CONTACT)),
  } as ContactDraft;
  const shown: ContactDraft = { ...fromCard, ...edits };
  const known = useKnownContact(shown.email);
  const projectShown: ProjectDraft = countryChosen
    ? answers
    : { ...answers, installation_country: shown.country };
  const name = [shown.first_name, shown.last_name].filter(Boolean).join(' ');

  function change(next: Partial<ContactDraft>) {
    setEdits(next);
    onEdits(next);
  }

  /** The contact, as it is saved: the fields given, and the read ones still to check. */
  function contactOp(changedAt: string, eventId: string | null): SyncOp {
    const fields: Record<string, unknown> = {};
    for (const f of CONTACT_FORM_FIELDS) {
      const v = shown[f as keyof ContactDraft];
      if (v === undefined || v === '' || v === false || (Array.isArray(v) && v.length === 0)) {
        continue;
      }
      fields[f] = typeof v === 'string' ? v.trim() : v;
    }
    const checked = toCheck.filter((f) => !(f in edits) && f in fields);
    return {
      opId: newId(),
      entity: 'contact',
      id: draft.id,
      action: 'upsert',
      changedAt,
      fields: {
        ...fields,
        ...(checked.length > 0 ? { needs_verification: checked } : {}),
        source: 'scan',
        event_id: eventId,
      },
    };
  }

  async function save(next: 'scan' | 'home') {
    if (saving) return;
    setSaving(true);
    setProblem(null);
    try {
      const eventId = await askEvent.eventForCapture();
      if (eventId === undefined) return setSaving(false);
      await saveScan(draft.photo, contactOp(new Date().toISOString(), eventId));
      toast(name ? `Contact saved: ${name}` : 'Contact saved. The card is read in the background.');
      onSaved(next);
    } catch {
      setProblem('This device could not save the contact. Nothing was lost: try again.');
      setSaving(false);
    }
  }

  async function saveLead() {
    if (saving) return;
    const errs = projectErrors(projectShown);
    setProjectErrs(errs ?? {});
    if (errs) return focusFirst(rootRef.current, errs);
    setSaving(true);
    setProblem(null);
    try {
      const eventId = await askEvent.eventForCapture();
      if (eventId === undefined) return;
      const changedAt = new Date().toISOString();
      const event = eventId ? await store?.getRecord('event', eventId) : null;
      const contact = contactOp(changedAt, eventId);
      // The company comes from the card: the worker fills it once read.
      const { ops } = leadOps({
        person: {
          id: draft.id,
          company: shown.company.trim() || null,
          country: shown.country || null,
          event_id: eventId,
          had: contact.action === 'upsert' ? contact.fields : {},
        },
        draft: projectShown,
        eventId,
        eventCode: (event?.event_code as string | undefined) ?? null,
        ownerId: user.id,
        qualify: can('leads.qualify'),
        stages,
        changedAt,
      });
      // The answers the contact did not have go with it, in the same change.
      const extra = ops.filter((op) => !(op.entity === 'contact' && op.action === 'upsert'));
      const own = ops.find((op) => op.entity === 'contact' && op.action === 'upsert');
      if (own?.action === 'upsert' && contact.action === 'upsert') {
        Object.assign(contact.fields, own.fields);
      }
      await saveScan(draft.photo, contact, ...extra);
      setDone(shown.company.trim() || name || 'the scanned card');
    } catch {
      setProblem('This device could not save the lead. Nothing was lost: try again.');
    } finally {
      setSaving(false);
    }
  }

  /** The person exists already: open it, or add the lead to it; this scan is not kept. */
  async function openKnown(lead: boolean) {
    if (!known) return;
    try {
      const id = await knownContactId(known);
      if (id) onDiscard(lead ? `/people/contacts/${id}/lead` : `/people/contacts/${id}`);
    } catch (error) {
      setProblem(errorMessage(error));
    }
  }

  if (done) {
    return (
      <div className="stage-sheet" role="status" style={{ flexDirection: 'column', gap: 12 }}>
        <b>Lead saved: {done}</b>
        <span className="note">
          It goes to HubSpot by itself once it is complete; what is missing is in To do.
        </span>
        <div className="g2" style={{ width: '100%' }}>
          <button type="button" className="btn primary block" onClick={() => onSaved('scan')}>
            <Icon name="scan" />
            Scan next
          </button>
          <button type="button" className="btn block" onClick={() => onSaved('home')}>
            Back to home
          </button>
        </div>
      </div>
    );
  }

  const status =
    reading === 'reading'
      ? null
      : reading === 'done'
        ? 'Read: check the fields, correct what is wrong.'
        : reading === 'typing'
          ? 'Type the details; the reading fills what you leave empty.'
          : reading === 'failed'
            ? 'The card could not be read: type the details, or later from To do.'
            : 'Reading when network is back. You can type now.';

  return (
    <div ref={rootRef}>
      <div className="stage-sheet">
        <img src={photoUrl} className="stage-thumb" alt="" />
        <div style={{ flex: 1 }} aria-live="polite">
          {reading === 'reading' ? (
            <>
              <span className="scan-reading">
                <span className="spinner" aria-hidden="true" />
                <b>Reading the card…</b>
              </span>
              <button
                type="button"
                className="btn sm"
                style={{ marginTop: 8 }}
                onClick={() => setReading('typing')}
              >
                Type it myself
              </button>
            </>
          ) : (
            <>
              <b>{name || 'The scanned card'}</b>
              <span className="note" style={{ display: 'block' }}>
                {status}
              </span>
            </>
          )}
        </div>
      </div>
      {known && <KnownContact known={known} onOpen={(lead) => void openKnown(lead)} />}
      <fieldset className="fs-plain" disabled={locked} aria-busy={locked}>
        <div className="fm dlg-form">
          <ContactForm
            value={shown}
            onChange={(v) => {
              const next: Partial<ContactDraft> = { ...edits };
              for (const f of CONTACT_FORM_FIELDS) {
                const k = f as keyof ContactDraft;
                if (JSON.stringify(v[k]) !== JSON.stringify(shown[k])) {
                  (next as Record<string, unknown>)[k] = v[k];
                }
              }
              change(next);
            }}
            errors={{}}
            moreOpen={moreOpen}
            onMoreToggle={setMoreOpen}
            leadAnswers={false}
          >
            {project && !known && (
              <ProjectBlock
                value={projectShown}
                onChange={(v) => {
                  if (v.installation_country !== projectShown.installation_country) {
                    setCountryChosen(true);
                  }
                  setAnswers(v);
                  if (Object.keys(projectErrs).length > 0) setProjectErrs({});
                }}
                errors={projectErrs}
                personName={shown.first_name}
              />
            )}
          </ContactForm>
        </div>
      </fieldset>
      {project && !known && (
        <button
          type="button"
          className="linkbtn"
          onClick={() => {
            setProject(false);
            setProjectErrs({});
          }}
        >
          No project: keep a contact only
        </button>
      )}
      {problem && <Notice kind="error">{problem}</Notice>}
      <div className="sticky-actions scan-bar" role="group" aria-label="Save the scan">
        {known ? (
          <button type="button" className="btn" onClick={onClose}>
            Discard this scan
          </button>
        ) : project ? (
          <button
            type="button"
            className="btn primary"
            disabled={saving || locked}
            onClick={() => void saveLead()}
          >
            Save
          </button>
        ) : (
          <>
            <button
              type="button"
              className="btn"
              disabled={saving || locked}
              onClick={() => void save('home')}
            >
              Save
            </button>
            <button
              type="button"
              className="btn primary"
              disabled={saving || locked}
              onClick={() => void save('scan')}
            >
              <Icon name="scan" />
              Scan next
            </button>
            {canLead && (
              <button
                type="button"
                className="btn"
                disabled={saving || locked}
                onClick={() => {
                  setProject(true);
                  setTimeout(() => {
                    const block = rootRef.current?.querySelector('[aria-label="Project"]');
                    if (block && 'scrollIntoView' in block)
                      block.scrollIntoView({ block: 'start' });
                  });
                }}
              >
                Lead
              </button>
            )}
          </>
        )}
      </div>
      {askEvent.dialog}
    </div>
  );
}

/** A new draft for a photo just confirmed. */
export function newDraft(photo: Blob): ScanDraft {
  return { id: newId(), photo, edits: {}, startedAt: Date.now() };
}
